/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-underscore-dangle */
import lod_ from "lodash";
import { createSlice } from "@reduxjs/toolkit";
import { createSelector } from "reselect";

// Initial state with an empty object for folders (normalized)
const initialState = { folders: {} };

const folderSlice = createSlice({
	name: "folder",
	initialState,
	reducers: {
		updateFolderByIdsPath: (state, action) => {
			const { path, update } = action.payload;

			// Validate input
			if (!Array.isArray(path) || path.length === 0) {
				return;
			}

			// Recursive function to traverse and update folder
			const traverseAndUpdate = (currentFolder, remainingPath) => {
				// If no more IDs in path, apply the update
				if (remainingPath.length === 0) {
					lod_.merge(currentFolder, update);
					return;
				}

				// Get the next ID in the path
				const [nextId, ...restOfPath] = remainingPath;

				// Check if current folder has children
				if (!currentFolder.children || !Array.isArray(currentFolder.children)) {
					return;
				}

				// Find the child with the target ID
				const childFolder = currentFolder.children.find(
					child => child.id === nextId || child._id === nextId
				);

				if (!childFolder) {
					return;
				}

				// Recursively traverse to the next level
				traverseAndUpdate(childFolder, restOfPath);
			};

			// Start with the root folder (first ID in path)
			const [rootId, ...remainingPath] = path;
			const rootFolder = state.folders[rootId];

			if (!rootFolder) {
				return;
			}

			// Start the recursive traversal
			traverseAndUpdate(rootFolder, remainingPath);
		},
		// Update a folder only if it has changed or doesn't exist
		updateFolder: (state, action) => {
			const currentFolder = state.folders[action.payload.id];
			if (!currentFolder || JSON.stringify(currentFolder) !== JSON.stringify(action.payload)) {
				state.folders[action.payload.id] = action.payload;
			}
		},
		// Delete a folder by ID
		deleteFolder: (state, action) => {
			delete state.folders[action.payload];
		},
		// Add a new folder to the object
		createFolder: (state, action) => {
			state.folders[action.payload.id] = action.payload;
		},
		// Replace all folders with a normalized object from an array
		setFolders: (state, action) => {
			state.folders = action.payload.reduce((acc, folder) => {
				acc[folder?.id] = folder;
				return acc;
			}, {});
		},
		// Update a specific element in the tree using dot-separated path
		updateTreeElement: (state, action) => {
			const { path, updates } = action.payload;
			const pathParts = path.split(".");

			// Find the root folder by key
			const rootKey = pathParts[0];
			const rootFolder = Object.values(state.folders).find(folder => folder.key === rootKey);

			if (!rootFolder) return;

			// If path has only one part, update the root folder
			if (pathParts.length === 1) {
				Object.assign(rootFolder, updates);
				return;
			}

			// Navigate through the tree using the remaining path parts
			let current = rootFolder;
			for (let i = 1; i < pathParts.length; i += 1) {
				const code = pathParts[i];

				if (!current.children) {
					return; // Path doesn't exist
				}

				// Find the child with the matching code
				const child = current.children.find(child => child.code === code);
				if (!child) {
					return; // Path doesn't exist
				}

				// If this is the last part of the path, update this element
				if (i === pathParts.length - 1) {
					Object.assign(child, updates);
					return;
				}

				// Move to the next level
				current = child;
			}
		}
	}
});

// Export the actions
export const {
	updateFolderByIdsPath,
	updateFolder,
	deleteFolder,
	createFolder,
	setFolders,
	updateTreeElement
} = folderSlice.actions;

// Base selector to get the folders as an object
export const selectFoldersObject = state => state.knowledgeBase.folder.folders;

// Selector to get folders as an array, sorted by rank
export const selectFolders = createSelector([selectFoldersObject], folders =>
	Object.values(folders).sort((a, b) => (a?.rank || 0) - (b?.rank || 0))
);

// Memoized selector to get a folder by ID
export const selectFolderById = createSelector(
	[selectFoldersObject, (_, folderId) => folderId],
	(folders, folderId) => folders[folderId] || null
);

// Memoized selector to filter folders by parent ID
export const selectFoldersByParentId = createSelector(
	[selectFolders, (_, parentId) => parentId],
	(folders, parentId) => folders.filter(folder => folder.parentId === parentId)
);

export const selectAllFoldersForTree = createSelector([selectFolders], folders => {
	return folders || [];
});

/**
 * Recursively find a folder by a path of IDs
 * @param {Object} foldersObj - The folders object from the state
 * @param {Array} path - Array of folder IDs representing the path
 * @returns {Object|null} - The found folder or null if not found
 */
export const selectFolderByPath = createSelector(
	[selectFoldersObject, (_, path) => path],
	(foldersObj, path) => {
		const traversePath = (currentFolder, remainingPath) => {
			if (remainingPath.length === 0) {
				return currentFolder;
			}

			const [nextId, ...restOfPath] = remainingPath;

			if (!currentFolder.children || !Array.isArray(currentFolder.children)) {
				return null;
			}

			const childFolder = currentFolder.children.find(
				child => child.id === nextId || child._id === nextId
			);

			if (!childFolder) {
				return null;
			}

			return traversePath(childFolder, restOfPath);
		};
		if (!Array.isArray(path) || path.length === 0) {
			return null;
		}

		const [rootId, ...remainingPath] = path;
		const rootFolder = foldersObj[rootId];

		if (!rootFolder) {
			return null;
		}

		return traversePath(rootFolder, remainingPath);
	}
);

/**
 * Find the root folder given any subfolder ID (can be deeply nested)
 * @param {Object} foldersObj - The folders object from the state
 * @param {string} subchildId - The ID of any subfolder
 * @returns {Object|null} - The root folder or null if not found
 */
export const selectRootFolderBySubchildId = createSelector(
	[selectFoldersObject, (_, subchildId) => subchildId],
	(foldersObj, subchildId) => {
		// Helper function to recursively search for a folder by ID
		const findFolderRecursively = (folder, targetId) => {
			// Check if current folder matches
			if (folder.id === targetId || folder._id === targetId) {
				return folder;
			}

			// Search in children
			if (folder.children && Array.isArray(folder.children)) {
				const found = folder.children.find(child => findFolderRecursively(child, targetId));
				if (found) {
					return findFolderRecursively(found, targetId);
				}
			}

			return null;
		};

		// Iterate through all root folders to find which one contains the subchild
		const rootFolders = Object.values(foldersObj);
		const matchingRoot = rootFolders.find(rootFolder =>
			findFolderRecursively(rootFolder, subchildId)
		);

		return matchingRoot || null;
	}
);

// Export the reducer
export default folderSlice.reducer;
